import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CodigoTipoSensor,
  IParticionZona,
  ISensorPorZona,
  ModoSensor,
} from 'modelos/src';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { TIPOS_SENSORES } from '../../alarmas-domiciliarias.service';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-agregar-infozona-completo',
  templateUrl: './agregar-infozona-completo.component.html',
  styleUrl: './agregar-infozona-completo.component.scss',
  standalone: false,
})
export class AgregarInfoZonaCompletoComponent implements OnInit {
  data = inject<{
    infoEditar?: IParticionZona;
    zonasExistentes?: IParticionZona[];
}>(MAT_DIALOG_DATA);
  helper = inject(HelperService);
  dialogRef = inject<MatDialogRef<AgregarInfoZonaCompletoComponent>>(MatDialogRef);

  public form?: FormGroup;
  public title?: string;
  public creandoNuevoTag?: boolean = false;
  public tipoSensores = TIPOS_SENSORES;
  public tipoSensoresLista: CodigoTipoSensor[] = [
    'PIR',
    'DRV',
    'MMG',
    'BIR',
    'PAS',
    'PPC',
    'TAM',
    'OCR',
    'HUM',
    'PFU',
    'ELE',
    'BUM',
    'CEM',
    'VOL',
    'DTS',
    'SIS',
    'AMK',
  ];
  // Opciones del selector de tipo: código + descripción
  public tipoSensoresItems = this.tipoSensoresLista.map((codigo) => ({
    valor: codigo,
    etiqueta: `${codigo} - ${this.tipoSensores[codigo]}`,
  }));
  public modoSensor: ModoSensor[] = ['Instantaneo', 'Demorado', 'Seguidor'];
  public sensoresSugeridos: ISensorPorZona[] = [];
  public marcasObs$: Observable<string[]>[] = [];
  public modelosObs$: Observable<string[]>[] = [];

  get sensoresArray(): FormArray {
    return this.form?.get('sensores') as FormArray;
  }

  private createSensorFormGroup(sensor?: ISensorPorZona): FormGroup {
    return new FormGroup({
      tipo: new FormControl(sensor?.tipo, Validators.required),
      marca: new FormControl(sensor?.marca, Validators.required),
      modelo: new FormControl(sensor?.modelo, Validators.required),
    });
  }

  private setupSensorObservables(i: number) {
    const group = this.sensoresArray.at(i) as FormGroup;
    const tipoCtrl = group.get('tipo');
    const marcaCtrl = group.get('marca');
    const modeloCtrl = group.get('modelo');

    this.marcasObs$[i] = marcaCtrl.valueChanges.pipe(
      startWith(marcaCtrl.value ?? ''),
      map((typed) => {
        const marcas = this.getAllMarcas();
        const filter = (typed ?? '').toLowerCase();
        return filter
          ? marcas.filter((m) => m.toLowerCase().includes(filter))
          : marcas;
      }),
    );

    this.modelosObs$[i] = combineLatest([
      tipoCtrl.valueChanges.pipe(startWith(tipoCtrl.value)),
      modeloCtrl.valueChanges.pipe(startWith(modeloCtrl.value ?? '')),
    ]).pipe(
      map(([tipo, typed]) => {
        const modelos = this.getAllModelos(tipo);
        const filter = (typed ?? '').toLowerCase();
        return filter
          ? modelos.filter((m) => m.toLowerCase().includes(filter))
          : modelos;
      }),
    );
  }

  private rebuildObservables() {
    this.marcasObs$ = [];
    this.modelosObs$ = [];
    for (let i = 0; i < this.sensoresArray.length; i++) {
      this.setupSensorObservables(i);
    }
  }

  private get todosLosSensores(): ISensorPorZona[] {
    return [...this.sensoresSugeridos, ...(this.sensoresArray?.value ?? [])];
  }

  private getAllMarcas(): string[] {
    return [
      ...new Set(this.todosLosSensores.map((s) => s.marca).filter(Boolean)),
    ];
  }

  private getAllModelos(tipo: string): string[] {
    return [
      ...new Set(
        this.todosLosSensores
          .filter((s) => !tipo || s.tipo === tipo)
          .map((s) => s.modelo)
          .filter(Boolean),
      ),
    ];
  }

  private createForm() {
    const sensoresArray = new FormArray<FormGroup>([]);
    if (this.data?.infoEditar?.sensores?.length) {
      this.data.infoEditar.sensores.forEach((s) => {
        sensoresArray.push(this.createSensorFormGroup(s));
      });
    } else {
      sensoresArray.push(this.createSensorFormGroup());
    }

    this.form = new FormGroup({
      nombre: new FormControl(
        this.data?.infoEditar?.nombre,
        Validators.required,
      ),
      particion: new FormControl(
        this.data?.infoEditar?.particion,
        Validators.required,
      ),
      zona: new FormControl(this.data?.infoEditar?.zona, Validators.required),
      modo: new FormControl(this.data?.infoEditar?.modo, Validators.required),
      sensores: sensoresArray,
    });
    this.rebuildObservables();
  }

  public addSensor() {
    this.sensoresArray.push(this.createSensorFormGroup());
    this.rebuildObservables();
  }

  public removeSensor(i: number) {
    this.sensoresArray.removeAt(i);
    this.rebuildObservables();
  }

  public async onSubmit() {
    this.dialogRef.close(this.form);
  }

  async ngOnInit() {
    this.title = this.data?.infoEditar ? 'Editar Zona' : 'Agregar Zona';
    this.sensoresSugeridos = (this.data?.zonasExistentes ?? [])
      .flatMap((z) => z.sensores ?? [])
      .filter((s) => s.tipo);
    this.createForm();
  }
}
